// Centralized pricing configuration for MicroFeedback
// This file centralizes all pricing, features, and plan details in one place for easy maintenance

export interface PlanFeature {
  name: string;
  description?: string;
}

export interface PricingPlan {
  id: 'free' | 'pro' | 'enterprise';
  name: string;
  tagline?: string;
  price: {
    monthly: number;
    annual: number;
  };
  displayPrice: {
    monthly: string;
    annual: string;
    annualMonthly: string; // Annual price shown as monthly equivalent
  };
  features: PlanFeature[];
  limits: {
    votesPerMonth: number | -1; // -1 means unlimited
    commentsPerMonth: number | -1;
    sites: number | -1;
    freePlanDurationDays?: number;
  };
  capabilities: {
    exportCsv: boolean;
    apiAccess: boolean;
    customBranding: boolean;
    emailAlerts: boolean;
    prioritySupport: boolean;
    dedicatedSupport: boolean;
    customIntegrations: boolean;
    whiteLabel: boolean;
    slaGuarantee: boolean;
    advancedAnalytics: boolean;
    basicAnalytics: boolean;
  };
  popular?: boolean;
  stripeIds?: {
    monthly?: string;
    annual?: string;
  };
}

// Annual discount percentage
export const ANNUAL_DISCOUNT = 0.04; // 4% discount

// Core pricing plans
export const PRICING_PLANS: Record<string, PricingPlan> = {
  free: {
    id: 'free',
    name: 'Free',
    tagline: '14-day trial',
    price: {
      monthly: 0,
      annual: 0,
    },
    displayPrice: {
      monthly: '$0',
      annual: '$0',
      annualMonthly: '$0',
    },
    features: [
      { name: 'Up to 1,000 votes per month' },
      { name: 'Basic analytics' },
      { name: 'Email support' },
      { name: '1 website' },
      { name: '14-day trial period' },
    ],
    limits: {
      votesPerMonth: 1000,
      commentsPerMonth: 500,
      sites: 1,
      freePlanDurationDays: 14,
    },
    capabilities: {
      exportCsv: false,
      apiAccess: false,
      customBranding: false,
      emailAlerts: false,
      prioritySupport: false,
      dedicatedSupport: false,
      customIntegrations: false,
      whiteLabel: false,
      slaGuarantee: false,
      advancedAnalytics: false,
      basicAnalytics: true,
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    tagline: 'Most popular',
    price: {
      monthly: 11.99,
      annual: 115.11,
    },
    displayPrice: {
      monthly: '$11.99',
      annual: '$115.11',
      annualMonthly: '$9.59', // $115.11 / 12 = $9.59
    },
    features: [
      { name: 'Unlimited votes' },
      { name: 'Unlimited websites' },
      { name: 'Advanced analytics' },
      { name: 'Email alerts' },
      { name: 'Data export (CSV)' },
      { name: 'Priority support' },
      { name: 'API access' },
      { name: 'Custom branding' },
      { name: 'Save 20% with annual billing' },
    ],
    limits: {
      votesPerMonth: -1, // Unlimited
      commentsPerMonth: -1, // Unlimited
      sites: -1, // Unlimited
    },
    capabilities: {
      exportCsv: true,
      apiAccess: true,
      customBranding: true,
      emailAlerts: true,
      prioritySupport: true,
      dedicatedSupport: false,
      customIntegrations: false,
      whiteLabel: false,
      slaGuarantee: false,
      advancedAnalytics: true,
      basicAnalytics: true,
    },
    popular: true,
    stripeIds: {
      monthly: import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID,
      annual: import.meta.env.VITE_STRIPE_PRO_ANNUAL_PRICE_ID,
    },
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For large organizations',
    price: {
      monthly: 99,
      annual: 948,
    },
    displayPrice: {
      monthly: '$99',
      annual: '$948',
      annualMonthly: '$79', // $948 / 12 = $79
    },
    features: [
      { name: 'Everything in Pro' },
      { name: 'Custom integrations' },
      { name: 'Dedicated support' },
      { name: 'White-label options' },
      { name: 'SLA guarantee' },
      { name: 'Custom branding' },
      { name: 'Advanced API access' },
      { name: 'Priority feature requests' },
      { name: 'Save 20% with annual billing' },
    ],
    limits: {
      votesPerMonth: -1, // Unlimited
      commentsPerMonth: -1, // Unlimited
      sites: -1, // Unlimited
    },
    capabilities: {
      exportCsv: true,
      apiAccess: true,
      customBranding: true,
      emailAlerts: true,
      prioritySupport: true,
      dedicatedSupport: true,
      customIntegrations: true,
      whiteLabel: true,
      slaGuarantee: true,
      advancedAnalytics: true,
      basicAnalytics: true,
    },
    stripeIds: {
      monthly: import.meta.env.VITE_STRIPE_ENTERPRISE_MONTHLY_PRICE_ID,
      annual: import.meta.env.VITE_STRIPE_ENTERPRISE_ANNUAL_PRICE_ID,
    },
  },
};

// Helper functions
export function getPlanById(planId: string): PricingPlan | undefined {
  return PRICING_PLANS[planId];
}

export function getAllPlans(): PricingPlan[] {
  return Object.values(PRICING_PLANS);
}

export function getPopularPlan(): PricingPlan | undefined {
  return getAllPlans().find(plan => plan.popular);
}

// Price formatting helpers
export function formatPrice(price: number): string {
  return price === 0 ? 'Free' : `$${price}`;
}

export function formatAnnualSavings(monthlyPrice: number, annualPrice: number): string {
  const annualTotal = monthlyPrice * 12;
  const savings = annualTotal - annualPrice;
  const percentage = Math.round((savings / annualTotal) * 100);
  return `Save ${percentage}% ($${savings.toFixed(2)})`;
}

// Feature comparison helpers
export function getFeatureComparison(): {
  feature: string;
  free: boolean | string;
  pro: boolean | string;
  enterprise: boolean | string;
}[] {
  return [
    {
      feature: 'Monthly votes',
      free: '1,000',
      pro: 'Unlimited',
      enterprise: 'Unlimited',
    },
    {
      feature: 'Websites',
      free: '1',
      pro: 'Unlimited',
      enterprise: 'Unlimited',
    },
    {
      feature: 'Basic analytics',
      free: true,
      pro: true,
      enterprise: true,
    },
    {
      feature: 'Advanced analytics',
      free: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: 'Email support',
      free: true,
      pro: true,
      enterprise: true,
    },
    {
      feature: 'Priority support',
      free: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: 'Dedicated support',
      free: false,
      pro: false,
      enterprise: true,
    },
    {
      feature: 'Email alerts',
      free: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: 'Data export (CSV)',
      free: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: 'API access',
      free: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: 'Custom branding',
      free: false,
      pro: true,
      enterprise: true,
    },
    {
      feature: 'Custom integrations',
      free: false,
      pro: false,
      enterprise: true,
    },
    {
      feature: 'White-label options',
      free: false,
      pro: false,
      enterprise: true,
    },
    {
      feature: 'SLA guarantee',
      free: false,
      pro: false,
      enterprise: true,
    },
  ];
}

// Types are already exported above with the interface declarations